import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { pollEvery } from '../realtime/live';

export type MessageStatus = 'QUEUED' | 'SENT' | 'FAILED' | 'SKIPPED';

export interface GuestMessage {
  id: string;
  bookingId: string | null;
  channel: 'SMS' | 'EMAIL' | 'WHATSAPP';
  to: string;
  kind: string;
  subject: string | null;
  body: string;
  status: MessageStatus;
  attempts: number;
  error: string | null;
  createdAt: string;
  sentAt: string | null;
  by: string;
  segments: number | null;
}

export interface MessageList {
  provider: string;
  canSend: boolean;
  trouble: number;
  messages: GuestMessage[];
}

export const KIND_LABEL: Record<string, string> = {
  REQUEST_RECEIVED: 'Request received',
  BOOKING_CONFIRMED: 'Booking confirmed',
  BOOKING_CANCELLED: 'Booking cancelled',
  ARRIVAL_REMINDER: 'Arrival reminder',
  MANUAL: 'Written by staff',
};

export const useMessages = (opts: { bookingId?: string; status?: MessageStatus } = {}) => {
  const qs = new URLSearchParams({ ...(opts.bookingId ? { bookingId: opts.bookingId } : {}), ...(opts.status ? { status: opts.status } : {}) }).toString();
  return useQuery({ queryKey: ['messages', opts.bookingId ?? '', opts.status ?? ''], refetchInterval: (q) => (q.state.data?.messages.some((m) => m.status === 'QUEUED') ? 5_000 : pollEvery(30_000)()), queryFn: () => api<MessageList>(`/messages${qs ? `?${qs}` : ''}`) });
};

export function useMessageMutation<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}
